/**
 * Компонент сканера QR-кодов с автоматическим включением при движении
 * Использует qr-scanner (Nimiq) - более быструю и точную библиотеку
 * БЕЗ КНОПОК - только автоматическое управление по движению
 * Использует качественный motion detector на основе Canvas API
 * Поддерживает настройки камеры через CSS фильтры (яркость, контрастность)
 */
import { useEffect, useRef, useState, useCallback, memo, useMemo } from 'react'
import QrScanner from 'qr-scanner'
import { CryptoService, type QRVerificationResult } from '../services/cryptoService'
import { EventsRepo } from '../offline/eventsRepo'
import { LogsRepo } from '../offline/logsRepo'
import { EmployeesRepo } from '../offline/employeesRepo'
import { useDeviceStore } from '../store/deviceStore'
import { useUIStore } from '../store/uiStore'
import { useCameraStore } from '../store/cameraStore'
import { MotionDetector } from '../utils/motionDetector'
import { Watchdog } from '../utils/watchdog'
import { withTimeout, TimeoutError } from '../utils/timeout'
import { AdminActionDialog } from './AdminActionDialog'
import { toMSKISOString } from '../utils/timezone'
import './Scanner.css'

interface ScannerProps {
  onScanSuccess?: (result: QRVerificationResult) => void
  onScanError?: (error: string) => void
}

const CAMERA_ACTIVE_TIME = 10000 // 10 секунд активной камеры
const FORCE_IDLE_TIMEOUT = 60000 // 60 секунд - принудительный переход в idle
const WATCHDOG_TIMEOUT = 60000 // 60 секунд - watchdog timeout
const QR_PROCESSING_TIMEOUT = 35000 // 35 секунд - максимальное время обработки QR
// verifyQRToken может выполнять emergency HTTP-sync когда сотрудник не найден в кеше.
// При медленной сети HTTP + bulkPut занимает 5-15 секунд.
// Поэтому для верификации QR используем отдельный таймаут.
const VERIFY_QR_TIMEOUT = 20000   // 20 секунд — только для verifyQRToken (включает emergency sync)
const OPERATION_TIMEOUT = 10000   // 10 секунд — для локальных операций (IDB, hash).
// 10s вместо 5s: IDB может быть медленным на фрагментированном SQLite.
// Реальный IDB-запрос должен занимать <100ms, но при фрагментации — дольше.
// После очистки БД (v10 migration + hourly cleanup) значение уменьшится снова.

// Функция для получения приветствия в зависимости от времени суток
const getGreeting = (): string => {
  const hour = new Date().getHours()
  if (hour >= 5 && hour < 12) return 'Доброе утро!'
  if (hour >= 12 && hour < 18) return 'Добрый день!'
  if (hour >= 18 && hour < 23) return 'Добрый вечер!'
  return 'Доброй ночи!'
}

// Singleton AudioContext — создаём один раз, переиспользуем.
// Создание нового AudioContext на каждый скан вызывает утечку памяти
// (браузер ограничивает ~6 контекстов, потом начинает тормозить).
let _sharedAudioContext: AudioContext | null = null

function getSharedAudioContext(): AudioContext | null {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioContextClass) return null
    if (!_sharedAudioContext || _sharedAudioContext.state === 'closed') {
      _sharedAudioContext = new AudioContextClass()
    }
    return _sharedAudioContext
  } catch {
    return null
  }
}

// Функция для воспроизведения звука beep
const playBeepSound = () => {
  try {
    const audioContext = getSharedAudioContext()
    if (!audioContext) return

    // Возобновляем если браузер приостановил (autoplay policy)
    const doPlay = () => {
      try {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)
        oscillator.frequency.value = 800
        oscillator.type = 'sine'
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1)
        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.1)
      } catch (err) {
        console.warn('Failed to play beep sound:', err)
      }
    }

    if (audioContext.state === 'suspended') {
      audioContext.resume().then(doPlay).catch(() => {})
    } else {
      doPlay()
    }
  } catch (err) {
    console.warn('Failed to play beep sound:', err)
  }
}

interface QRCornerPoint {
  x: number
  y: number
}

export const Scanner = memo(function Scanner({ onScanSuccess, onScanError }: ScannerProps) {
  const [isScanning, setIsScanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<QRVerificationResult | null>(null)
  const [_isIdle, setIsIdle] = useState(true) // Начинаем с idle экрана (используется для внутренней логики)
  const [scanningQR, setScanningQR] = useState(false)
  const [qrDetected, setQrDetected] = useState(false) // Флаг обнаружения QR для подсветки
  const [qrCorners, setQrCorners] = useState<QRCornerPoint[] | null>(null) // Координаты углов QR для AR-точек
  const [employeeName, setEmployeeName] = useState<string | null>(null) // Имя сотрудника для плашки
  const [checkTime, setCheckTime] = useState<string | null>(null) // Время отметки для плашки
  const [accessDeniedError, setAccessDeniedError] = useState<string | null>(null) // Ошибка доступа для большой красной плашки
  const [currentTime, setCurrentTime] = useState<string>('') // Текущее время для часов
  const [currentDate, setCurrentDate] = useState<string>('') // Текущая дата
  const [currentWeekday, setCurrentWeekday] = useState<string>('') // Текущий день недели
  const [showAdminActionDialog, setShowAdminActionDialog] = useState(false) // Диалог выбора действия для админа
  const [adminQRData, setAdminQRData] = useState<{ qrText: string; employeeId: string; employeeName: string } | null>(null) // Данные админского QR
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const scannerRef = useRef<QrScanner | null>(null)
  const motionDetectorRef = useRef<MotionDetector | null>(null)
  const watchdogRef = useRef<Watchdog | null>(null) // Watchdog для восстановления зависшего состояния
  const deviceStore = useDeviceStore()
  const uiStore = useUIStore()
  const cameraStore = useCameraStore()
  const cameraTimeoutRef = useRef<number | null>(null)
  const idleTimeoutRef = useRef<number | null>(null)
  const processingQRRef = useRef(false) // Флаг обработки QR для предотвращения повторной обработки
  const qrProcessingTimeoutRef = useRef<number | null>(null) // Timeout на обработку QR
  const errorTimeoutRef = useRef<number | null>(null) // Таймер для автоскрытия ошибок
  const mediaStreamRef = useRef<MediaStream | null>(null) // Сохраняем MediaStream
  const forceIdleTimeoutRef = useRef<number | null>(null) // Таймер принудительного перехода в idle
  const qrSessionIdRef = useRef(0) // Монотонный счётчик сессий обработки QR (для предотвращения race condition)

  // CSS фильтры для видео (GPU-ускорение, работает на всех устройствах)
  const videoFilters = useMemo(() => {
    const cameraSettings = cameraStore.settings
    const filters: string[] = []
    
    // Brightness: 128 = 100% (норма), 0 = 0%, 255 = 200%
    const brightnessPercent = (cameraSettings.brightness / 128) * 100
    // Exposure compensation: -3 to +3 -> дополнительная яркость
    const exposureBrightness = 100 + (cameraSettings.exposureCompensation * 15)
    // Комбинируем brightness и exposure
    const totalBrightness = (brightnessPercent / 100) * (exposureBrightness / 100) * 100
    filters.push(`brightness(${totalBrightness}%)`)
    
    // Contrast: 128 = 100% (норма), 0 = 0%, 255 = 200%
    const contrastPercent = (cameraSettings.contrast / 128) * 100
    filters.push(`contrast(${contrastPercent}%)`)
    
    // Saturation: 100 = норма, 0 = grayscale, 200 = очень насыщенно
    filters.push(`saturate(${cameraSettings.saturation}%)`)
    
    // Grayscale: 0% = цветное, 100% = черно-белое
    if (cameraSettings.grayscale) {
      filters.push('grayscale(100%)')
    }
    
    // Invert: 0% = норма, 100% = инверсия
    if (cameraSettings.invert) {
      filters.push('invert(100%)')
    }
    
    return filters.join(' ')
  }, [cameraStore.settings])

  // Применяем CSS фильтры к video элементу при изменении настроек
  useEffect(() => {
    if (videoRef.current && isScanning) {
      videoRef.current.style.filter = videoFilters
      console.log('[Scanner] Applied CSS filter:', videoFilters)
    }
  }, [videoFilters, isScanning])

  const handleQRCode = useCallback(async (qrText: string, cornerPoints?: QRCornerPoint[]) => {
    // Предотвращаем повторную обработку одного и того же QR
    if (processingQRRef.current) {
      return
    }
    
    // 🔒 КРИТИЧНО: Устанавливаем флаг СРАЗУ для предотвращения race condition
    processingQRRef.current = true
    // Уникальный ID текущей сессии обработки QR — защищает от race condition
    // когда QR_PROCESSING_TIMEOUT срабатывает, пока handleQRCode ещё выполняется
    const sessionId = ++qrSessionIdRef.current
    
    // Фильтрация шума: проверяем что это похоже на наш токен
    // TOTP: hex строка длиной 32 символа (16 байт * 2) - НОВЫЙ ФОРМАТ
    // JWT: начинается с "eyJ" (base64 кодированный JSON header), минимум 50 символов
    // Binary V1: hex строка длиной 150 символов (75 байт * 2)
    // Binary V2: hex строка длиной 146 символов (73 байт * 2)
    const isTOTP = qrText && qrText.length === 32 && /^[0-9a-fA-F]+$/.test(qrText)
    const isJWT = qrText && qrText.length >= 50 && qrText.startsWith('eyJ')
    const isBinaryV1 = qrText && qrText.length === 150 && /^[0-9a-fA-F]+$/.test(qrText)
    const isBinaryV2 = qrText && qrText.length === 146 && /^[0-9a-fA-F]+$/.test(qrText)
    const isBinary = isBinaryV1 || isBinaryV2
    
    if (!isTOTP && !isJWT && !isBinary) {
      console.log('[Scanner] Ignored invalid QR format (possible noise):', {
        length: qrText?.length || 0,
        preview: qrText?.substring(0, 20) || '',
        isTOTP,
        isJWT,
        isBinary
      })
      // НЕ показываем ошибки для левых QR - просто игнорируем
      // Сбрасываем флаг для следующего сканирования
      processingQRRef.current = false
      return
    }
    
    console.log('[Scanner] Valid QR detected:', {
      format: isTOTP ? 'TOTP' : (isJWT ? 'JWT' : 'Binary'),
      length: qrText.length
    })
    
    // Устанавливаем timeout на обработку QR (защита от зависания)
    // Проверяем sessionId — если обработка уже завершилась, не трогаем состояние
    qrProcessingTimeoutRef.current = window.setTimeout(async () => {
      // Если processingQRRef уже сброшен — обработка завершилась нормально, пропускаем
      if (!processingQRRef.current) return
      // Если сессия сменилась (новая обработка уже началась) — пропускаем
      if (qrSessionIdRef.current !== sessionId) return

      console.error('[Scanner] 🚨 QR processing TIMEOUT! Force reset...')
      
      await LogsRepo.save('error', 'QR processing TIMEOUT triggered', {
        timeout_ms: QR_PROCESSING_TIMEOUT,
        timestamp: new Date().toISOString()
      }).catch(err => console.error('Failed to log timeout:', err))
      
      processingQRRef.current = false
      setScanningQR(false)
      setQrDetected(false)
      setQrCorners(null)
      setError('Ошибка обработки QR-кода (timeout)')
    }, QR_PROCESSING_TIMEOUT)
    
    try {
      // Сбрасываем watchdog при каждой QR-обработке
      watchdogRef.current?.reset()
      
      // Показываем анимацию сканирования и подсветку
      setScanningQR(true)
      setQrDetected(true)
      
      // Устанавливаем координаты углов для AR-точек
      if (cornerPoints && cornerPoints.length >= 4) {
        setQrCorners(cornerPoints)
      }
      
      // Воспроизводим звук beep
      playBeepSound()
      
      // НЕ останавливаем поток кадров! Делаем задержку 1 секунду
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Проверяем QR-код через криптографический сервис.
      // Используем VERIFY_QR_TIMEOUT (не OPERATION_TIMEOUT): внутри verifyQRToken
      // может выполняться emergency HTTP-sync если сотрудник не найден в кеше.
      const result = await withTimeout(
        CryptoService.verifyQRToken(qrText, deviceStore.siteId || undefined),
        VERIFY_QR_TIMEOUT,
        'QR verification timeout'
      )

      setIsIdle(false)

      if (result.valid && result.employeeId && result.payload) {
        // Проверяем, является ли это админским QR-кодом
        if (result.role === 'admin') {
          // Загружаем данные сотрудника для проверки прав
          let employeeData = null
          let adminName = 'Администратор'
          try {
            employeeData = await withTimeout(
              EmployeesRepo.getFullData(result.employeeId),
              OPERATION_TIMEOUT,
              'Employee data fetch timeout'
            )
            if (employeeData?.full_name) {
              adminName = employeeData.full_name
            }
          } catch (err) {
            console.warn('[Scanner] Failed to load employee data:', err)
          }
          
          // Проверяем права: можно ли открыть админ-панель?
          // Диалог показываем только если есть права на планшет ИЛИ разработчик
          const canAccessTablet = employeeData?.can_access_tablet || false
          const isDeveloper = employeeData?.is_developer || false
          
          if (canAccessTablet || isDeveloper) {
            // У админа есть права - показываем диалог выбора действия
            // НЕ устанавливаем lastResult, чтобы не показывать плашку успеха
            setAdminQRData({
              qrText,
              employeeId: result.employeeId,
              employeeName: adminName,
            })
            setShowAdminActionDialog(true)
            
            await LogsRepo.save('info', 'Admin with tablet access: showing action dialog', {
              employee_id: result.employeeId,
              can_access_tablet: canAccessTablet,
              is_developer: isDeveloper,
            })
            
            // Останавливаем сканирование до выбора действия
            if (scannerRef.current) {
              await scannerRef.current.stop()
              scannerRef.current = null
            }
            setIsScanning(false)
            setScanningQR(false)
            setQrDetected(false)
            setQrCorners(null)
            processingQRRef.current = false
            return
          } else {
            // У админа НЕТ прав на планшет - отмечаем как обычного сотрудника
            console.log('[Scanner] Admin without tablet access: processing as regular employee')
            await LogsRepo.save('info', 'Admin without tablet access: check-in as regular employee', {
              employee_id: result.employeeId,
            })
            // НЕ делаем return - продолжаем обработку как для обычного сотрудника
          }
        }

        // Устанавливаем lastResult для обычных сотрудников и админов без прав
        setLastResult(result)

        // Сохраняем событие в IndexedDB для обычных сотрудников
        let eventType: 'check_in' | 'check_out' = 'check_in'
        if (result.employeeId && deviceStore.siteId && deviceStore.deviceId) {
          eventType = await withTimeout(
            EventsRepo.determineNextEventType(
              result.employeeId,
              deviceStore.siteId
            ),
            OPERATION_TIMEOUT,
            'Determine event type timeout'
          )

          const happenedAt = toMSKISOString()

          const payloadHash = await withTimeout(
            CryptoService.calculatePayloadHash({
              employee_id: result.employeeId,
              site_id: deviceStore.siteId,
              device_id: deviceStore.deviceId,
              event_type: eventType,
              happened_at: happenedAt,
              qr_token: qrText,
            }),
            OPERATION_TIMEOUT,
            'Payload hash calculation timeout'
          )

          try {
            await withTimeout(
              EventsRepo.save({
                employee_id: result.employeeId,
                site_id: deviceStore.siteId,
                device_id: deviceStore.deviceId,
                event_type: eventType,
                source: 'scan',
                happened_at: happenedAt,
                payload_hash: payloadHash,
                qr_token: qrText,
              }),
              OPERATION_TIMEOUT,
              'Event save timeout'
            )
            console.log(`[Scanner] Event saved: ${eventType} for employee ${result.employeeId}`)
            
            // Немедленная синхронизация если есть подключение к интернету
            if (navigator.onLine) {
              const { SyncService } = await import('../services/syncService')
              SyncService.syncNow().catch((syncError) => {
                console.error('[Scanner] Failed to sync immediately:', syncError)
                // Не показываем ошибку пользователю - событие будет синхронизировано позже
              })
            }
          } catch (saveError) {
            console.error('[Scanner] Failed to save event:', saveError)
            // Продолжаем выполнение даже если сохранение не удалось
          }
        }

        // Загружаем имя сотрудника для плашки
        let name = 'Сотрудник'
        try {
          const employeeData = await withTimeout(
            EmployeesRepo.getFullData(result.employeeId),
            OPERATION_TIMEOUT,
            'Employee name fetch timeout'
          )
          if (employeeData?.full_name) {
            name = employeeData.full_name
          }
        } catch (err) {
          console.warn('[Scanner] Failed to load employee name:', err)
        }

        // Форматируем время отметки
        const checkTimeFormatted = new Date().toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })

        setEmployeeName(name)
        setCheckTime(checkTimeFormatted)

        // Сохраняем тип события в result для отображения
        setLastResult({
          ...result,
          eventType,
        })

        onScanSuccess?.(result)
        await LogsRepo.save('info', 'QR code scanned successfully', {
          employee_id: result.employeeId,
          role: result.role,
          event_type: eventType,
        })

        // Показываем экран результата на 3 секунды, затем сбрасываем
        setTimeout(() => {
          setScanningQR(false)
          setQrDetected(false)
          setQrCorners(null)
          processingQRRef.current = false
          // Скрываем результат через 3 секунды
          setTimeout(() => {
            setLastResult(null)
            setEmployeeName(null)
            setCheckTime(null)
          }, 3000)
        }, 3000)
      } else {
        const errorMsg = result.error || 'Invalid QR code'
        console.log('[Scanner] Invalid QR result:', errorMsg)
        
        // Проверяем тип ошибки - для ошибок доступа показываем большую красную плашку
        const isAccessDeniedError = errorMsg.includes('Employee not found') || 
                                     errorMsg.includes('не найден') ||
                                     errorMsg.includes('Доступ запрещен') ||
                                     errorMsg.includes('Недостаточно прав')
        
        if (isAccessDeniedError) {
          // Определяем более конкретное сообщение для ошибки
          let errorTitle = 'Доступ запрещен!'
          if (errorMsg.includes('Employee not found') || errorMsg.includes('не найден')) {
            errorTitle = 'Сотрудник удален из системы!'
          }
          
          // Показываем большую красную плашку для ошибок доступа
          setAccessDeniedError(errorTitle)
          
          // Автоматически скрываем через 5 секунд (увеличено с 3)
          if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current)
          }
          errorTimeoutRef.current = window.setTimeout(() => {
            setAccessDeniedError(null)
            errorTimeoutRef.current = null
          }, 5000)
        }
        // Для других невалидных QR (не наши форматы, старые токены и т.д.) - НЕ показываем ошибки
        
        onScanError?.(errorMsg)
        await LogsRepo.save('warn', 'Invalid QR code scanned', {
          error: errorMsg,
          qr_text: qrText.substring(0, 20),
        })
        
        // КРИТИЧНО: Немедленно освобождаем флаг для следующего сканирования
        processingQRRef.current = false
        
        // Сбрасываем анимацию и подсветку через 0.5 секунды (визуальный эффект)
        setTimeout(() => {
          setScanningQR(false)
          setQrDetected(false)
          setQrCorners(null)
        }, 500)
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to process QR code'
      
      console.log('[Scanner] QR processing error:', errorMsg)
      
      // Очищаем предыдущий таймер если есть
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current)
        errorTimeoutRef.current = null
      }
      
      // СПЕЦИАЛЬНАЯ обработка TimeoutError (операция зависла)
      if (err instanceof TimeoutError) {
        console.error('[Scanner] 🚨 OPERATION TIMEOUT:', errorMsg)
        setAccessDeniedError('Ошибка обработки! Попробуйте снова')
        errorTimeoutRef.current = window.setTimeout(() => {
          setAccessDeniedError(null)
          errorTimeoutRef.current = null
        }, 3000)
        
        // Немедленно освобождаем для следующей попытки
        processingQRRef.current = false
        setScanningQR(false)
        setQrDetected(false)
        setQrCorners(null)
        return
      }
      
      // Проверяем тип ошибки - ТОЛЬКО для валидных Recvisit QR показываем сообщения
      const isAccessDeniedError = errorMsg.includes('Employee not found') || 
                                   errorMsg.includes('не найден') ||
                                   errorMsg.includes('Доступ запрещен') ||
                                   errorMsg.includes('Недостаточно прав')
      
      const isInvalidToken = errorMsg.includes('Invalid') || 
                             errorMsg.includes('expired') ||
                             errorMsg.includes('истек')
      
      if (isAccessDeniedError) {
        // Показываем большую красную плашку по центру для ошибок доступа
        setAccessDeniedError('Доступ запрещен!')
        
        // Автоматически скрываем через 3 секунды
        errorTimeoutRef.current = window.setTimeout(() => {
          setAccessDeniedError(null)
          errorTimeoutRef.current = null
        }, 3000)
      } else if (isInvalidToken) {
        // Для невалидных токенов - маленькая ошибка внизу (может быть просто старый QR)
        setError('Невалидный или устаревший QR-код')
      
        // Автоматически скрываем ошибку через 3 секунды
        errorTimeoutRef.current = setTimeout(() => {
          const errorElement = document.querySelector('.scanner-error')
          if (errorElement) {
            errorElement.classList.add('fade-out')
            setTimeout(() => {
              setError(null)
              errorTimeoutRef.current = null
            }, 500)
          } else {
            setError(null)
            errorTimeoutRef.current = null
          }
        }, 3000)
      }
      // Для всех остальных ошибок (например "Unexpected token") - НИЧЕГО не показываем
      // Это может быть просто чужой QR-код
      
      onScanError?.(errorMsg)
      // ВАЖНО: Сбрасываем анимацию и подсветку ВСЕГДА
      setScanningQR(false)
      setQrDetected(false)
      setQrCorners(null)
      processingQRRef.current = false // КРИТИЧНО: освобождаем флаг для следующего сканирования
    } finally {
      // КРИТИЧНО: всегда очищаем timeout независимо от результата
      if (qrProcessingTimeoutRef.current) {
        clearTimeout(qrProcessingTimeoutRef.current)
        qrProcessingTimeoutRef.current = null
      }
      
      // Сбрасываем watchdog после завершения обработки QR
      watchdogRef.current?.reset()
    }
  }, [deviceStore.siteId, deviceStore.deviceId, uiStore, onScanSuccess, onScanError])

  // Обработчик "Отметиться" для админа
  const handleAdminCheckIn = useCallback(async () => {
    if (!adminQRData) return

    try {
      setShowAdminActionDialog(false)
      setScanningQR(true)

      // Обрабатываем отметку как для обычного сотрудника (с timeout!)
      let eventType: 'check_in' | 'check_out' = 'check_in'
      if (adminQRData.employeeId && deviceStore.siteId && deviceStore.deviceId) {
        eventType = await withTimeout(
          EventsRepo.determineNextEventType(
            adminQRData.employeeId,
            deviceStore.siteId
          ),
          OPERATION_TIMEOUT,
          'Admin event type determination timeout'
        )

        const happenedAt = toMSKISOString()

        const payloadHash = await withTimeout(
          CryptoService.calculatePayloadHash({
            employee_id: adminQRData.employeeId,
            site_id: deviceStore.siteId,
            device_id: deviceStore.deviceId,
            event_type: eventType,
            happened_at: happenedAt,
            qr_token: adminQRData.qrText,
          }),
          OPERATION_TIMEOUT,
          'Admin payload hash timeout'
        )

        try {
          await withTimeout(
            EventsRepo.save({
            employee_id: adminQRData.employeeId,
            site_id: deviceStore.siteId,
            device_id: deviceStore.deviceId,
            event_type: eventType,
            source: 'scan',
            happened_at: happenedAt,
            payload_hash: payloadHash,
            qr_token: adminQRData.qrText,
          }),
          OPERATION_TIMEOUT,
          'Admin event save timeout'
        )
          console.log(`[Scanner] Admin check-in event saved: ${eventType}`)
          
          // Немедленная синхронизация если есть подключение к интернету
          if (navigator.onLine) {
            const { SyncService } = await import('../services/syncService')
            SyncService.syncNow().catch((syncError) => {
              console.error('[Scanner] Failed to sync admin event immediately:', syncError)
              // Не показываем ошибку пользователю - событие будет синхронизировано позже
            })
          }
        } catch (saveError) {
          console.error('[Scanner] Failed to save admin check-in event:', saveError)
        }
      }

      // Показываем плашку с результатом
      setEmployeeName(adminQRData.employeeName)
      setCheckTime(new Date().toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
      }))
      
      setLastResult({
        valid: true,
        employeeId: adminQRData.employeeId,
        eventType,
        role: 'admin',
        payload: undefined,
      })

      await LogsRepo.save('info', 'Admin checked in via QR code', {
        employee_id: adminQRData.employeeId,
        event_type: eventType,
      })

      // Сбрасываем данные админа
      setAdminQRData(null)
      setScanningQR(false)
      setQrDetected(false)
      setQrCorners(null)
      processingQRRef.current = false

      // Автоматически скрываем результат через 3 секунды
      setTimeout(() => {
        setLastResult(null)
        setEmployeeName(null)
        setCheckTime(null)
      }, 3000)
    } catch (err) {
      console.error('[Scanner] Admin check-in failed:', err)
      
      // Специальная обработка timeout
      if (err instanceof TimeoutError) {
        setError('Операция зависла! Попробуйте снова')
      } else {
        setError('Не удалось отметить администратора')
      }
      
      setShowAdminActionDialog(false)
      setAdminQRData(null)
      setScanningQR(false)
      processingQRRef.current = false
    }
  }, [adminQRData, deviceStore.siteId, deviceStore.deviceId])

  // Обработчик "Админ-панель" для админа
  const handleAdminPanelOpen = useCallback(async () => {
    if (!adminQRData) return

    try {
      // Переключаемся в админ-режим
      uiStore.setAdminMode(true)
      
      await LogsRepo.save('info', 'Admin opened admin panel via QR code', {
        employee_id: adminQRData.employeeId,
      })

      // Сбрасываем состояние
      setShowAdminActionDialog(false)
      setAdminQRData(null)
      setScanningQR(false)
      setQrDetected(false)
      setQrCorners(null)
      processingQRRef.current = false
    } catch (err) {
      console.error('[Scanner] Failed to open admin panel:', err)
      setError('Не удалось открыть админ-панель')
      setShowAdminActionDialog(false)
      setAdminQRData(null)
      processingQRRef.current = false
    }
  }, [adminQRData, uiStore])

  const stopScanning = useCallback(async () => {
    if (!isScanning || !scannerRef.current) {
      return
    }

    try {
      const scanner = scannerRef.current
      await scanner.stop()
      
      // Останавливаем MediaStream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop())
        mediaStreamRef.current = null
      }
      
      scannerRef.current = null
      setIsScanning(false)
      
      // Устанавливаем таймер для перехода в idle через 2 секунды
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current)
      }
      idleTimeoutRef.current = window.setTimeout(() => {
        setIsIdle(true)
      }, 2000)
      
      if (cameraTimeoutRef.current) {
        clearTimeout(cameraTimeoutRef.current)
        cameraTimeoutRef.current = null
      }
    } catch (err) {
      console.error('Failed to stop scanner:', err)
      
      // Останавливаем MediaStream даже при ошибке
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop())
        mediaStreamRef.current = null
      }
      
      setIsScanning(false)
      setIsIdle(true)
      scannerRef.current = null
      if (cameraTimeoutRef.current) {
        clearTimeout(cameraTimeoutRef.current)
        cameraTimeoutRef.current = null
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current)
        idleTimeoutRef.current = null
      }
    }
  }, [isScanning])

  const startScanning = useCallback(async () => {
    if (isScanning || !videoRef.current) return

    try {
      // Очищаем предыдущие ошибки при запуске камеры
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current)
        errorTimeoutRef.current = null
      }
      setError(null)
      setAccessDeniedError(null)
      setLastResult(null)
      setIsIdle(false) // Выходим из idle при запуске камеры

      // Убеждаемся, что видео элемент видим перед запуском сканера
      if (videoRef.current) {
        videoRef.current.style.display = 'block'
        videoRef.current.style.visibility = 'visible'
      }

      // Получаем настройки камеры из store
      const cameraSettings = cameraStore.settings
      console.log('[Scanner] Using camera settings:', cameraSettings)

      const scanner = new QrScanner(
        videoRef.current,
        async (result) => {
          // Получаем координаты углов QR-кода для AR-точек
          // QrScanner с returnDetailedScanResult: true возвращает объект ScanResult
          const qrText = typeof result === 'string' ? result : (result as any).data || ''
          let cornerPoints: QRCornerPoint[] | undefined
          
          if (typeof result === 'object' && result !== null) {
            const detailedResult = result as any
            if (Array.isArray(detailedResult.cornerPoints) && detailedResult.cornerPoints.length >= 4) {
              cornerPoints = detailedResult.cornerPoints.map((p: any) => ({
                x: typeof p === 'object' && p !== null ? (p.x || p[0] || 0) : 0,
                y: typeof p === 'object' && p !== null ? (p.y || p[1] || 0) : 0,
              }))
            }
          }
          
          await handleQRCode(qrText, cornerPoints)
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: false, // Отключаем встроенную подсветку
          highlightCodeOutline: false, // Отключаем встроенную обводку
          maxScansPerSecond: cameraSettings.maxScansPerSecond, // Из настроек (оптимизация)
          preferredCamera: cameraSettings.preferredCamera === 'environment' ? 'environment' : 'user',
          // Не логируем ошибки "No QR code found"
          onDecodeError: () => {
            // Сбрасываем AR-точки когда QR не найден
            setQrCorners(null)
            setQrDetected(false)
          },
        }
      )

      scannerRef.current = scanner
      await scanner.start()
      
      // Сохраняем MediaStream
      if (videoRef.current && videoRef.current.srcObject) {
        mediaStreamRef.current = videoRef.current.srcObject as MediaStream
      }
      
      // Применяем CSS фильтры (яркость, контрастность)
      // Hardware constraints не поддерживаются на фронтальных камерах
      if (videoRef.current) {
        videoRef.current.style.filter = videoFilters
        console.log('[Scanner] Applied CSS filter:', videoFilters)
      }
      
      // Дополнительная проверка что видео поток запущен
      if (videoRef.current) {
        videoRef.current.play().catch((err) => {
          console.warn('Video play error:', err)
        })
      }
      
      setIsScanning(true)
      setIsIdle(false) // Гарантируем выход из idle
      
      // Дополнительно убеждаемся что idle сброшен через небольшую задержку
      setTimeout(() => {
        setIsIdle(false)
      }, 100)
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to start camera'
      
      // Очищаем предыдущий таймер если есть
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current)
        errorTimeoutRef.current = null
      }
      
      setError(errorMsg)
      setIsScanning(false)
      setIsIdle(true) // Возвращаемся в idle при ошибке
      onScanError?.(errorMsg)
      
      // Автоматически скрываем ошибку через 5 секунд с fade-out
      errorTimeoutRef.current = setTimeout(() => {
        const errorElement = document.querySelector('.scanner-error')
        if (errorElement) {
          errorElement.classList.add('fade-out')
          setTimeout(() => {
            setError(null)
            errorTimeoutRef.current = null
          }, 500) // Ждем завершения fade-out анимации
        } else {
          setError(null)
          errorTimeoutRef.current = null
        }
      }, 5000)
    }
  }, [isScanning, handleQRCode, onScanError, cameraStore])

  // WATCHDOG для восстановления зависшего состояния
  useEffect(() => {
    const watchdog = new Watchdog(
      WATCHDOG_TIMEOUT,
      async () => {
        console.error('[Scanner] 🚨 WATCHDOG TRIGGERED! Scanner appears frozen. Forcing recovery...')
        
        // 🔍 DIAGNOSTIC: Логируем watchdog trigger для исследования
        await LogsRepo.save('error', 'WATCHDOG TRIGGERED - scanner frozen', {
          watchdog_timeout_ms: WATCHDOG_TIMEOUT,
          timestamp: new Date().toISOString(),
          was_processing_qr: processingQRRef.current
        }).catch(err => console.error('Failed to log watchdog:', err))
        
        // Принудительное восстановление
        processingQRRef.current = false
        setScanningQR(false)
        setQrDetected(false)
        setQrCorners(null)
        setShowAdminActionDialog(false)
        setAdminQRData(null)
        setError(null)
        setAccessDeniedError(null)
        
        // Останавливаем и перезапускаем scanner
        if (scannerRef.current) {
          try {
            scannerRef.current.stop()
            scannerRef.current = null
          } catch (err) {
            console.error('[Scanner] Error stopping scanner:', err)
          }
        }
        
        setIsScanning(false)
        
        // Очищаем все таймеры
        if (cameraTimeoutRef.current) clearTimeout(cameraTimeoutRef.current)
        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current)
        if (qrProcessingTimeoutRef.current) clearTimeout(qrProcessingTimeoutRef.current)
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current)
        if (forceIdleTimeoutRef.current) clearTimeout(forceIdleTimeoutRef.current)
        
        console.log('[Scanner] ✅ Recovery complete. Scanner reset to idle state.')
      },
      'ScannerWatchdog'
    )
    
    watchdogRef.current = watchdog
    watchdog.start()
    
    return () => {
      watchdog.stop()
      watchdogRef.current = null
    }
  }, [])
  
  // Сбрасываем watchdog при любой активности пользователя
  useEffect(() => {
    const resetWatchdog = () => {
      watchdogRef.current?.reset()
    }
    
    window.addEventListener('click', resetWatchdog)
    window.addEventListener('touchstart', resetWatchdog)
    window.addEventListener('keydown', resetWatchdog)
    
    return () => {
      window.removeEventListener('click', resetWatchdog)
      window.removeEventListener('touchstart', resetWatchdog)
      window.removeEventListener('keydown', resetWatchdog)
    }
  }, [])
  
  // Детекция движения через качественный motion detector
  useEffect(() => {
    // Проверяем доступность motion detector
    if (!MotionDetector.isAvailable()) {
      console.warn('Motion detector not available, using fallback')
      // Fallback: используем клик для активации камеры
      const handleClick = () => {
        if (!isScanning) {
          startScanning()
        }
      }
      window.addEventListener('click', handleClick)
      return () => {
        window.removeEventListener('click', handleClick)
      }
    }

    // Создаем motion detector с настраиваемой чувствительностью
    const motionDetector = new MotionDetector({
      threshold: cameraStore.settings.motionSensitivity, // Настраиваемая чувствительность (0-255, выше = менее чувствительно)
      sampleRate: 250, // Анализ каждые 250мс - реже проверяем
      minMotionFrames: 5, // Требуется 5 кадров с движением - больше подтверждений
    })

    motionDetectorRef.current = motionDetector

    // Запускаем motion detector
    motionDetector
      .start(() => {
        // Движение обнаружено - активируем камеру сканирования
        console.log('[Scanner] Motion detected, resetting camera timeout')
        
        // Сбрасываем watchdog при обнаружении движения
        watchdogRef.current?.reset()
        
        if (!isScanning) {
          startScanning()
        }

        // Сбрасываем таймер выключения камеры
        if (cameraTimeoutRef.current) {
          clearTimeout(cameraTimeoutRef.current)
          cameraTimeoutRef.current = null
        }

        // Устанавливаем таймер выключения камеры через 10 секунд после последнего движения
        cameraTimeoutRef.current = window.setTimeout(() => {
          console.log('[Scanner] Camera timeout reached, stopping scanner')
          stopScanning()
        }, CAMERA_ACTIVE_TIME)
      })
      .catch((err) => {
        console.error('Failed to start motion detector:', err)
        // Fallback: используем клик для активации камеры
        const handleClick = () => {
          if (!isScanning) {
            startScanning()
          }
        }
        window.addEventListener('click', handleClick)
        return () => {
          window.removeEventListener('click', handleClick)
        }
      })

    return () => {
      // Останавливаем motion detector при размонтировании
      motionDetector.stop()
      motionDetectorRef.current = null
      if (cameraTimeoutRef.current) {
        clearTimeout(cameraTimeoutRef.current)
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current)
      }
      stopScanning()
    }
  }, [startScanning, stopScanning, cameraStore.settings.motionSensitivity]) // ← Убрали isScanning! Теперь пересоздается только при изменении чувствительности

  // Гарантируем отображение idle экрана при монтировании и когда камера не активна
  useEffect(() => {
    if (!isScanning) {
      setIsIdle(true) // Устанавливаем idle когда камера не активна
      
      // Очищаем ошибку при переходе в idle режим
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current)
        errorTimeoutRef.current = null
      }
      setError(null) // Скрываем ошибку при переходе в idle
      
      // Используем setTimeout для применения стиля после рендеринга
      const timer = setTimeout(() => {
        const idleElement = document.querySelector('.scanner-idle') as HTMLElement
        if (idleElement) {
          idleElement.style.setProperty('display', 'flex', 'important')
        }
      }, 0)
      return () => clearTimeout(timer)
    } else {
      setIsIdle(false) // Сбрасываем idle когда камера активна
    }
  }, [isScanning])

  // Обновление текущего времени, даты и дня недели для idle экрана
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date()
      
      // Время
      setCurrentTime(
        now.toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit',
        })
      )
      
      // Дата в формате "Среда, 10 декабря"
      const weekday = now.toLocaleDateString('ru-RU', { weekday: 'long' })
      const day = now.toLocaleDateString('ru-RU', { day: 'numeric' })
      
      // Месяцы в родительном падеже (с маленькой буквы)
      const monthsGenitive = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
      ]
      const month = monthsGenitive[now.getMonth()]
      
      setCurrentWeekday(weekday.charAt(0).toUpperCase() + weekday.slice(1)) // Первая буква заглавная
      setCurrentDate(`${day} ${month}`)
    }

    updateDateTime() // Обновляем сразу
    const intervalId = setInterval(updateDateTime, 1000) // Обновляем каждую секунду

    return () => clearInterval(intervalId)
  }, [])

  // Принудительный переход в idle если камера висит долго и не в админке
  useEffect(() => {
    // Очищаем предыдущий таймер
    if (forceIdleTimeoutRef.current) {
      clearTimeout(forceIdleTimeoutRef.current)
      forceIdleTimeoutRef.current = null
    }
    
    // Устанавливаем таймер только если:
    // 1. Камера активна (isScanning)
    // 2. НЕ в режиме админки
    // 3. НЕ показывается диалог выбора действия
    if (isScanning && !uiStore.isAdminMode && !showAdminActionDialog) {
      forceIdleTimeoutRef.current = window.setTimeout(() => {
        console.log('[Scanner] Force idle timeout reached, stopping scanner')
        stopScanning()
      }, FORCE_IDLE_TIMEOUT)
    }
    
    return () => {
      if (forceIdleTimeoutRef.current) {
        clearTimeout(forceIdleTimeoutRef.current)
        forceIdleTimeoutRef.current = null
      }
    }
  }, [isScanning, uiStore.isAdminMode, showAdminActionDialog, stopScanning])

  useEffect(() => {
    return () => {
      // Очистка при размонтировании
      if (scannerRef.current) {
        scannerRef.current.stop()
      }
      if (motionDetectorRef.current) {
        motionDetectorRef.current.stop()
      }
      if (cameraTimeoutRef.current) {
        clearTimeout(cameraTimeoutRef.current)
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current)
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current)
      }
      if (forceIdleTimeoutRef.current) {
        clearTimeout(forceIdleTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="scanner-container">
      {/* Видео элемент всегда создан, показывается когда камера активна */}
      <video 
        ref={videoRef} 
        className="scanner-video" 
        style={{ 
          display: isScanning ? 'block' : 'none',
          visibility: isScanning ? 'visible' : 'hidden'
        }}
        playsInline
        muted
        autoPlay
      />

      {/* Большие часы на экране сканирования */}
      {isScanning && (
        <div className="scanner-clock-overlay">
          <div className="scanner-clock">{currentTime}</div>
        </div>
      )}

      {/* Рамка сканирования с затемненным фоном - показываем когда камера активна */}
      {isScanning && (
        <div 
          className="qr-scan-overlay" 
          ref={(el) => {
            if (el) {
              el.style.setProperty('display', 'flex', 'important')
              el.style.setProperty('visibility', 'visible', 'important')
              el.style.setProperty('opacity', '1', 'important')
            }
          }}
        >
          <div className={`qr-scan-box ${scanningQR ? 'scanning' : ''} ${qrDetected ? 'qr-detected' : ''}`}>
            {/* Анимация сканирования ВНУТРИ рамки - всегда показываем анимацию когда камера активна */}
            <div className="qr-scan-animation-inner">
              <div className="qr-point qr-point-1"></div>
              <div className="qr-point qr-point-2"></div>
              <div className="qr-point qr-point-3"></div>
              <div className="qr-point qr-point-4"></div>
              <div className="qr-scan-line"></div>
            </div>
          </div>
        </div>
      )}

      {/* AR-точки которые прилипают к углам QR-кода */}
      {isScanning && qrCorners && qrCorners.length >= 4 && videoRef.current && (
        <div className="ar-points-overlay">
          {qrCorners.map((point, index) => {
            // Получаем размеры видео для правильного позиционирования
            const video = videoRef.current
            if (!video) return null
            
            const videoRect = video.getBoundingClientRect()
            const videoWidth = video.videoWidth || videoRect.width
            const videoHeight = video.videoHeight || videoRect.height
            
            // Масштабируем координаты из координат видео в координаты экрана
            const scaleX = videoRect.width / videoWidth
            const scaleY = videoRect.height / videoHeight
            
            const x = point.x * scaleX
            const y = point.y * scaleY
            
            return (
              <div
                key={index}
                className="ar-point"
                style={{
                  position: 'fixed',
                  left: `${x}px`,
                  top: `${y}px`,
                  transform: 'translate(-50%, -50%)',
                }}
              />
            )
          })}
        </div>
      )}

      {/* Экран idle - показываем когда камера НЕ активна */}
      {!isScanning && (
        <div className="scanner-idle">
          <div className="idle-content">
            <div className="idle-time">{currentTime}</div>
            <div className="idle-weekday-date">{currentWeekday}, {currentDate}</div>
            <div className="idle-logo">RECVISIT</div>
            <div className="idle-hint">Поднесите устройство для сканирования</div>
          </div>
        </div>
      )}

      {/* Текст "Сканирование..." */}
      {scanningQR && (
        <div className="scan-text-overlay">
          <div className="scan-text">Сканирование...</div>
        </div>
      )}

      {/* Подсказка под QR-боксом - показываем только когда камера активна и нет диалога */}
      {isScanning && !scanningQR && !showAdminActionDialog && (
        <div className="qr-hint">
          <p>📱 Поднесите QR-код к камере</p>
        </div>
      )}

      {error && (
        <div className="scanner-error" key={error}>
          <p>{error}</p>
        </div>
      )}

      {lastResult && lastResult.valid && (
        <div className={`scanner-success ${lastResult.eventType === 'check_out' ? 'scanner-success--checkout' : ''}`}>
          <div className="success-content">
            {lastResult.eventType === 'check_in' ? (
              <>
                <h2 className="success-title">{getGreeting()} ✅</h2>
                {employeeName && (
                  <div className="success-employee-name">{employeeName}</div>
                )}
                <p className="success-message">Отметка на объекте успешна</p>
                {checkTime && (
                  <div className="success-time">{checkTime}</div>
                )}
              </>
            ) : (
              <>
                <h2 className="success-title">👋 До свидания!</h2>
                {employeeName && (
                  <div className="success-employee-name">{employeeName}</div>
                )}
                <p className="success-message">Отметка на объекте успешна</p>
                {checkTime && (
                  <div className="success-time">{checkTime}</div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {lastResult && !lastResult.valid && (
        <div className="scanner-error">
          <p>❌ {lastResult.error || 'Невалидный QR-код'}</p>
        </div>
      )}

      {/* Большая красная плашка для ошибок доступа */}
      {accessDeniedError && (
        <div className="scanner-access-denied">
          <div className="access-denied-content">
            <h2 className="access-denied-title">🚫 {accessDeniedError}</h2>
            {/* Подпись показываем только для реальных ошибок доступа/не найден, не для timeout */}
            {!accessDeniedError.includes('Ошибка обработки') && (
              <p className="access-denied-message">
                {accessDeniedError.includes('удален') 
                  ? 'Сотрудник был удален из системы администратором'
                  : 'Сотрудник не найден в системе'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Диалог выбора действия для админа */}
      {showAdminActionDialog && adminQRData && (
        <AdminActionDialog
          employeeName={adminQRData.employeeName}
          onCheckIn={handleAdminCheckIn}
          onOpenAdminPanel={handleAdminPanelOpen}
          onClose={() => {
            console.log('[Scanner] AdminActionDialog closed manually')
            setShowAdminActionDialog(false)
            setAdminQRData(null)
            processingQRRef.current = false
            setScanningQR(false)
            setQrDetected(false)
            setQrCorners(null)
            
            // Очищаем timeout обработки QR
            if (qrProcessingTimeoutRef.current) {
              clearTimeout(qrProcessingTimeoutRef.current)
              qrProcessingTimeoutRef.current = null
            }
            
            // Сбрасываем watchdog
            watchdogRef.current?.reset()
          }}
        />
      )}
    </div>
  )
})
